import { Timestamp } from "firebase/firestore";
import i18n from "@/i18n";
import { APP_LOCALE } from "./locale";

type DateInput =
  | Date
  | Timestamp
  | { seconds: number; nanoseconds?: number }
  | string
  | number
  | null
  | undefined;

type NumberInput = number | string | null | undefined;

const DATE_ONLY = new Intl.DateTimeFormat(APP_LOCALE, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const DATETIME = new Intl.DateTimeFormat(APP_LOCALE, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const DATETIME_LONG = new Intl.DateTimeFormat(APP_LOCALE, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const TIME_ONLY = new Intl.DateTimeFormat(APP_LOCALE, {
  hour: "2-digit",
  minute: "2-digit",
});

const INT_FORMATTER = new Intl.NumberFormat(APP_LOCALE, { maximumFractionDigits: 0 });
const DECIMAL_FORMATTER = new Intl.NumberFormat(APP_LOCALE, { maximumFractionDigits: 2 });
const DEFAULT_FORMATTER = new Intl.NumberFormat(APP_LOCALE);

/**
 * Formatters for UI-facing labels (weekday name, day number, short month) follow
 * vue-i18n's locale rather than APP_LOCALE, so they stay in step with the
 * interface language instead of the browser's. Cached per locale+options so a
 * runtime language switch is picked up without rebuilding on every call.
 */
const uiFormatters = new Map<string, Intl.DateTimeFormat>();

function uiFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = i18n.global.locale.value;
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = uiFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    uiFormatters.set(key, formatter);
  }
  return formatter;
}

export function normalizeDate(input: DateInput): Date | null {
  if (!input) return null;
  if (input instanceof Date) return input;
  if (input instanceof Timestamp) return input.toDate();
  if (typeof input === "object" && "seconds" in input) {
    const s = (input as { seconds: number }).seconds;
    const ns = (input as { nanoseconds?: number }).nanoseconds ?? 0;
    return new Date(s * 1000 + ns / 1_000_000);
  }
  if (typeof input === "string") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatDateOnly(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return DATE_ONLY.format(d);
}

export function formatDateTime(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return DATETIME.format(d);
}

export function formatDateTimeLong(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return DATETIME_LONG.format(d);
}

export function formatFilenameDate(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatRelative(input: DateInput): string {
  const date = normalizeDate(input);
  if (!date) return "";

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = startOfToday.getTime() - startOfDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const hh = date.getHours().toString().padStart(2, "0");
  const mm = date.getMinutes().toString().padStart(2, "0");

  if (diffDays === 0) {
    return `Today ${hh}:${mm}`;
  } else if (diffDays === 1) {
    return `Yesterday ${hh}:${mm}`;
  } else if (diffDays <= 7) {
    return `${uiFormatter({ weekday: "long" }).format(date)} ${hh}:${mm}`;
  }
  return `${diffDays} days ago`;
}

export function formatWeekday(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return uiFormatter({ weekday: "long" }).format(d);
}

export function formatDay(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return uiFormatter({ day: "numeric" }).format(d);
}

export function formatMonthShort(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return uiFormatter({ month: "short" }).format(d);
}

export function formatTime(input: DateInput): string {
  const d = normalizeDate(input);
  if (!d) return "";
  return TIME_ONLY.format(d);
}

export function getTimePassed(date: Date): { year: number; month: number; days: number } {
  const today = new Date();

  let years = today.getFullYear() - date.getFullYear();
  let months = today.getMonth() - date.getMonth();
  let days = today.getDate() - date.getDate();

  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }

  if (months < 0) {
    months += 12;
    years -= 1;
  }

  return { year: years, month: months, days };
}

export function getDaysPassed(date: Date): number {
  const today = new Date();
  const startOfToday = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );
  const startOfDate = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

  const diffMs = startOfToday.getTime() - startOfDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return Math.max(Math.floor(diffDays), 0);
}

export function formatTimePassed(date: Date): string {
  const { year, month, days } = getTimePassed(date);

  if (!year && !month && !days) {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours >= 1) return `${hours} h`;
    if (minutes >= 1) return `${minutes} min`;
    return "1 min";
  }

  let str = "";
  if (days) {
    str = `${days} d ${str}`;
  }
  if (month) {
    str = `${month} mo ${str}`;
  }
  if (year) {
    str = `${year} yr ${str}`;
  }
  return str.trim();
}

export const INDICATE_SERVICE_OVER_MONTHS = 6;

export function serviceIsDue(date: Date): boolean {
  const { year, month, days } = getTimePassed(date);
  if (year) return true;
  if (month > INDICATE_SERVICE_OVER_MONTHS) return true;
  if (month === INDICATE_SERVICE_OVER_MONTHS && days) return true;
  return false;
}

export function formatInt(n: NumberInput): string {
  if (n == null || n === "" || isNaN(Number(n))) return "";
  return INT_FORMATTER.format(Number(n));
}

export function formatDecimal(n: NumberInput): string {
  if (n == null || n === "" || isNaN(Number(n))) return "";
  return DECIMAL_FORMATTER.format(Number(n));
}

export function formatNumber(n: NumberInput): string {
  if (n == null || n === "" || isNaN(Number(n))) return "";
  return DEFAULT_FORMATTER.format(Number(n));
}
